import { useCallback, useEffect, useRef, useState } from 'react';
import type { LatencyRow } from '../lib/api.js';
import { fetchLatencyRecent } from '../lib/api.js';
import {
  type DashboardResource,
  createDashboardResource,
  markResourceError,
  markResourceLoading,
  markResourceSuccess,
  updateResourceStaleness,
} from '../lib/dashboardResource.js';
import { useDashboard } from '../lib/store.js';

interface UseLatencyFeedOptions {
  pollIntervalMs?: number;
  staleAfterMs?: number;
}

interface UseLatencyFeedResult {
  latency: DashboardResource<LatencyRow[]>;
  refreshLatency: () => Promise<void>;
}

export function useLatencyFeed({
  pollIntervalMs = 60_000,
  staleAfterMs = 90_000,
}: UseLatencyFeedOptions = {}): UseLatencyFeedResult {
  const { setLatency } = useDashboard();
  const [latency, setLatencyResource] = useState(createDashboardResource<LatencyRow[]>());
  const mountedRef = useRef(true);

  const refreshLatency = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    setLatencyResource((resource) => markResourceLoading(resource));
    try {
      const payload = await fetchLatencyRecent();
      if (!mountedRef.current) {
        return;
      }
      setLatency(payload);
      setLatencyResource((resource) =>
        markResourceSuccess(resource, payload, new Date().toISOString()),
      );
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      setLatencyResource((resource) => markResourceError(resource, error));
    }
  }, [setLatency]);

  useEffect(() => {
    mountedRef.current = true;
    refreshLatency();
    const interval = setInterval(() => {
      refreshLatency();
    }, pollIntervalMs);

    const freshness = setInterval(() => {
      setLatencyResource((resource) => updateResourceStaleness(resource, staleAfterMs));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      clearInterval(freshness);
    };
  }, [pollIntervalMs, staleAfterMs, refreshLatency]);

  return { latency, refreshLatency };
}
